import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import "./home.css";
import { JusticeScene } from "@/components/JusticeScene";

export function Home() {
  const { status } = useAuth();
  const destination = status === "authed" ? "/matters" : "/login";
  return (
    <div className="public-home">
      <a className="home-skip" href="#main">
        Skip to content
      </a>
      <header className="home-header home-wrap">
        <Link to="/" className="home-brand" aria-label="Court Co-Pilot home">
          <img src="/lady-justice-emblem.webp" alt="" width="44" height="44" />
          <span>
            Court <strong>Co-Pilot</strong>
            <small>PREPARE WITH CONFIDENCE.</small>
          </span>
        </Link>
        <nav aria-label="Public navigation">
          <a href="#how-it-works">How it works</a>
          <a href="#inside">The workspace</a>
          <Link className="home-nav-signin" to={destination}>
            {status === "authed" ? "My matters" : "Sign in"}{" "}
            <ArrowRight size={16} />
          </Link>
        </nav>
      </header>
      <main id="main">
        <section className="home-hero home-wrap">
          <div className="home-hero-copy">
            <p className="home-eyebrow">
              <span /> A CLEARER WAY FORWARD
            </p>
            <h1>
              Your case.
              <br />
              <em>Your voice.</em>
            </h1>
            <p className="home-lead">
              A considered workspace for people representing themselves. Bring
              your evidence into focus, prepare your position and find your
              footing in the hearing.
            </p>
            <div className="home-actions">
              <Link className="home-button" to={destination}>
                {status === "authed"
                  ? "Open my workspace"
                  : "Sign in to your workspace"}{" "}
                <ArrowRight size={19} />
              </Link>
              <a className="home-text-link" href="#how-it-works">
                Explore how it works <ChevronRight size={17} />
              </a>
            </div>
            <p className="home-access">
              Private, invitation-only access. Public registration is not yet
              open.
            </p>
          </div>
          <JusticeScene />
        </section>
        <div className="home-proof">
          <div className="home-wrap">
            <span>
              <Check size={17} /> Matter-based workspace
            </span>
            <span>
              <Check size={17} /> Live hearing transcript
            </span>
            <span>
              <Check size={17} /> Evidence retrieval
            </span>
            <span>
              <Check size={17} /> Saved hearing history
            </span>
          </div>
        </div>
        <section id="how-it-works" className="home-section home-wrap">
          <div className="home-section-heading">
            <p className="home-eyebrow">LESS TO JUGGLE</p>
            <h2>
              A simple workflow.
              <br />
              <em>For a demanding day.</em>
            </h2>
            <p>
              Keep the important things within reach before, during and after
              your hearing.
            </p>
          </div>
          <div className="home-steps">
            {[
              [
                "01",
                "Choose your matter",
                "Open the case you are working on. Keep its documents and context together, separate from your other matters.",
              ],
              [
                "02",
                "Prepare your position",
                "Add documents, review your key points and record what you want the court to decide.",
              ],
              [
                "03",
                "Start your hearing",
                "Check your microphone and session, then follow the transcript. Pause listening whenever you need to.",
              ],
            ].map(([n, title, body]) => (
              <article key={n}>
                <span>{n}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </section>
        <section id="inside" className="home-inside">
          <div className="home-wrap home-inside-grid">
            <div>
              <p className="home-eyebrow">HELP, WHEN YOU NEED IT</p>
              <h2>
                Three useful actions.
                <br />
                <em>One focused workspace.</em>
              </h2>
              <p>
                Specialist tools remain available when you need more detail. The
                hearing screen keeps the immediate task clear.
              </p>
              <Link to={destination} className="home-text-link">
                Open Court Co-Pilot <ArrowRight size={18} />
              </Link>
            </div>
            <div className="home-action-list">
              <article>
                <span>01</span>
                <div>
                  <h3>Help me respond</h3>
                  <p>
                    Ask for a concise suggested response using your matter and
                    the latest transcript. Review it before speaking.
                  </p>
                </div>
              </article>
              <article>
                <span>02</span>
                <div>
                  <h3>Find my evidence</h3>
                  <p>
                    Search relevant document passages, including material that
                    may challenge your position. Inspect the source.
                  </p>
                </div>
              </article>
              <article>
                <span>03</span>
                <div>
                  <h3>Back to my key points</h3>
                  <p>
                    Return to your objectives and main points while staying
                    attentive to the question being asked.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>
        <section className="home-section home-wrap home-principles">
          <div>
            <p className="home-eyebrow">DESIGNED FOR REAL RESPONSIBILITY</p>
            <h2>
              Assistance you can
              <br />
              <em>question and check.</em>
            </h2>
          </div>
          <div>
            <h3>You stay in control.</h3>
            <p>
              Court Co-Pilot assists with preparation and understanding. It is
              not a lawyer and does not guarantee a legal outcome. Check
              quotations, evidence and legal references against the original
              sources.
            </p>
            <h3>Use listening with care.</h3>
            <p>
              Live transcription depends on your device, connection and
              available services. Check the words appearing on screen. Use
              recording or transcription only where the court permits it.
            </p>
          </div>
        </section>
        <section className="home-faq home-wrap">
          <p className="home-eyebrow">A FEW THINGS TO KNOW</p>
          <h2>Before you begin</h2>
          <details>
            <summary>Can I sign up today?</summary>
            <p>
              Access is currently by invitation. Existing users can sign in
              above. Public registration and a general release are still being
              prepared.
            </p>
          </details>
          <details>
            <summary>Will this tell me exactly what to say?</summary>
            <p>
              It can suggest a response and help locate material. Suggestions
              can be incomplete or wrong. You decide what to say, and should
              check the evidence and seek legal advice where possible.
            </p>
          </details>
          <details>
            <summary>Can I use it on my phone or tablet?</summary>
            <p>
              The web workspace adapts to phone, tablet and desktop screens.
              Test audio and microphone permissions on your actual device before
              a hearing. A native iOS app is not yet available.
            </p>
          </details>
          <details>
            <summary>Does an AI subscription cover this service?</summary>
            <p>
              A consumer AI subscription does not automatically include API
              usage. The private release uses application-configured services;
              public plans and user-selected providers are not yet offered.
            </p>
          </details>
        </section>
        <section className="home-final">
          <div className="home-wrap">
            <div>
              <p className="home-eyebrow">YOUR NEXT STEP</p>
              <h2>Come back to your case.</h2>
              <p>Your documents. Your key points. A place to prepare.</p>
            </div>
            <Link className="home-button home-button-light" to={destination}>
              Open your workspace <ArrowRight size={19} />
            </Link>
          </div>
        </section>
      </main>
      <footer className="home-footer home-wrap">
        <Link to="/" className="home-brand">
          <img src="/lady-justice-emblem.webp" alt="" width="34" height="34" />
          <span>
            Court <strong>Co-Pilot</strong>
          </span>
        </Link>
        <p>
          Preparation and hearing assistance for self-represented litigants.
        </p>
        <Link to="/login">Account sign in</Link>
        <small>© {new Date().getFullYear()} Court Co-Pilot</small>
      </footer>
    </div>
  );
}
